import * as cellRenderers from './cellRenderers';

const ERROR_STYLE = {
  backgroundColor: '#E23549',
  color: 'white',
};

const WARNING_STYLE = {
  backgroundColor: '#FFCC00',
  color: 'black',
};

const OK_STYLE = {
  backgroundColor: '#3DC873',
  color: 'black',
};

const INFO_STYLE = {
  backgroundColor: '#4F7BFF',
  color: 'white',
};

const NEUTRAL_STYLE = {};
const ANOMALY_WARNING_THRESHOLD = 50;

const ANOMALY_SCORE_KEYS = [
  'anomaly_score',
  'anomalyScore',
];

const RECOMMENDATIONS_ENABLED_KEYS = [
  'recommendationsEnabledBlockCount',
  'recommendations_enabled_block_count',
  'recEnabledBlockCount',
  'rec_enabled_block_count',
];

const RECOMMENDATIONS_HEALTHY_KEYS = [
  'recommendationsHealthyBlockCount',
  'recommendations_healthy_block_count',
  'recommendationsStatusHealthyBlockCount',
  'recHealthyBlockCount',
  'rec_healthy_block_count',
];

const IMAGERY_ENABLED_KEYS = [
  'imageryEnabledBlockCount',
  'imagery_enabled_block_count',
  'imageryEnabledCount',
];

const IMAGERY_HEALTHY_KEYS = [
  'imageryHealthyBlockCount',
  'imagery_healthy_block_count',
  'imageryStatusHealthyBlockCount',
  'imagery_status_healthy_block_count',
];

const getFirstNumber = (data, keys) => {
  for (let i = 0; i < keys.length; i += 1) {
    const value = data?.[keys[i]];
    if (value !== undefined && value !== null && value !== '') {
      const numericValue = Number(value);
      if (!Number.isNaN(numericValue)) return numericValue;
    }
  }
  return null;
};

const getTotalBlocks = (data) => getFirstNumber(data, [
  'block_count',
  'blockCount',
  'blocksCount',
  'totalBlocks',
  'totalBlockCount',
]);

const getRatioValues = (data, numeratorKeys) => {
  const total = getTotalBlocks(data);
  const numerator = getFirstNumber(data, numeratorKeys);
  if (total === null || numerator === null) return null;
  return { numerator, total };
};

const getCountValues = (data, onlineKeys, totalKeys) => {
  const online = getFirstNumber(data, onlineKeys);
  const total = getFirstNumber(data, totalKeys);
  if (online === null || total === null) return null;
  return { online, total };
};

const getGatewayStyleFromRatio = (ratio) => {
  if (!ratio || ratio.total <= 0) return NEUTRAL_STYLE;
  if (ratio.numerator <= 0) return ERROR_STYLE;
  if (ratio.numerator < ratio.total) return WARNING_STYLE;
  return OK_STYLE;
};

const getCountStyle = (counts) => {
  if (!counts) return NEUTRAL_STYLE;

  const {
    online,
    total,
  } = counts;

  if (total <= 0) return NEUTRAL_STYLE;
  if (online <= 0) return ERROR_STYLE;
  if (online < total) return WARNING_STYLE;
  return OK_STYLE;
};

const getRatioSortValue = (ratio) => {
  if (!ratio || ratio.total <= 0) return -1;
  return ratio.numerator / ratio.total;
};

const getCountSortValue = (counts) => {
  if (!counts || counts.total <= 0) return -1;
  return counts.online / counts.total;
};

const getDisplayValue = (value) => (
  value === undefined || value === null || value === '' ? '-' : value
);

const getLocationValue = (data) => {
  const latitude = data?.latitude;
  const longitude = data?.longitude;
  const hasLatitude = latitude !== undefined && latitude !== null;
  const hasLongitude = longitude !== undefined && longitude !== null;
  if (!hasLatitude || !hasLongitude) {
    return '';
  }
  return `${latitude}, ${longitude}`;
};

const locationCellRenderer = (params) => {
  const location = getLocationValue(params.data);
  if (!location) return '';

  const latitude = Number(params.data.latitude);
  const longitude = Number(params.data.longitude);
  const displayLatitude = Number.isFinite(latitude)
    ? latitude.toFixed(5) : params.data.latitude;
  const displayLongitude = Number.isFinite(longitude)
    ? longitude.toFixed(5) : params.data.longitude;
  const href = `https://www.google.com/maps/search/${latitude},${longitude}`;
  const label = `${displayLatitude}, ${displayLongitude}`;
  return `<a href="${href}" target="_blank">${label}</a>`;
};

const getAnomalyScoreValue = (data) => getFirstNumber(data, ANOMALY_SCORE_KEYS);

const getAnomalyScoreStyle = (score) => {
  if (score === null) return NEUTRAL_STYLE;
  if (score <= 0) return INFO_STYLE;
  if (score < ANOMALY_WARNING_THRESHOLD) return WARNING_STYLE;
  return ERROR_STYLE;
};

const anomalyScoreCellRenderer = (params) => {
  const score = getAnomalyScoreValue(params.data);
  if (score === null) {
    return '<span>-</span>';
  }
  return `<span>${score}</span>`;
};

const getRecommendationTooltip = (data, healthyKeys, enabledKeys, label) => {
  const healthyRatio = getRatioValues(data, healthyKeys);
  const enabledRatio = getRatioValues(data, enabledKeys);
  if (!healthyRatio || healthyRatio.total <= 0) return `${label}: no data available`;

  const healthyText = `${healthyRatio.numerator} of ${healthyRatio.total} blocks healthy`;
  const enabledText = enabledRatio
    ? `${enabledRatio.numerator} of ${enabledRatio.total} blocks enabled`
    : 'Enabled block count unavailable';
  return `${label}\n${healthyText}\n${enabledText}`;
};

const irrigRecCellRenderer = (params) => {
  const healthyRatio = getRatioValues(params.data, RECOMMENDATIONS_HEALTHY_KEYS);
  if (!healthyRatio || healthyRatio.total <= 0) {
    return '';
  }
  const ratioText = `${healthyRatio.numerator} / ${healthyRatio.total}`;
  return `<span>${ratioText}</span>`;
};

const satelliteImgCellRenderer = (params) => {
  const healthyRatio = getRatioValues(params.data, IMAGERY_HEALTHY_KEYS);
  if (!healthyRatio || healthyRatio.total <= 0) {
    return '';
  }
  const ratioText = `${healthyRatio.numerator} / ${healthyRatio.total}`;
  return `<span>${ratioText}</span>`;
};

const getConnectedDeviceTooltip = (label, counts) => {
  if (!counts || counts.total <= 0) return `${label}: no devices available`;
  return `${label}: ${counts.online} of ${counts.total} online`;
};

const connectedDeviceCellRenderer = (counts) => {
  if (!counts) {
    return '';
  }

  const {
    online,
    total,
  } = counts;
  const ratioText = `${online} / ${total}`;

  if (total <= 0) {
    return '';
  }

  return `<span>${ratioText}</span>`;
};

export const ranchColumnDefs = {
  index: {
    headerName: '#',
    field: 'index',
    cellRenderer: (params) => params.rowIndex + 1,
    suppressMenu: true,
    sortable: false,
    pinned: 'left',
    width: 40,
  },
  ranchId: {
    headerName: 'Ranch ID',
    pinned: 'left',
    width: 64,
    headerClass: 'ranch-compact-sort-header',
    valueGetter: (params) => getDisplayValue(params.data?.id),
  },
  name: {
    pinned: 'left',
    width: 140,
    minWidth: 140,
    maxWidth: 140,
  },
  entityName: {
    headerName: 'Entity Name',
    width: 150,
    minWidth: 150,
    maxWidth: 150,
    cellRenderer: cellRenderers.ranchEntityNameCopyCellRenderer,
  },
  entityId: {
    headerName: 'Entity ID',
    width: 52,
    valueGetter: (params) => getDisplayValue(params.data?.entity_id ?? params.data?.entityId),
  },
  entityPriority: {
    headerName: 'Entity\nPriority',
    wrapHeaderText: true,
    autoHeaderHeight: true,
    width: 52,
    headerClass: 'ranch-compact-sort-header',
    valueGetter: (params) => (
      getDisplayValue(
        params.data?.entityPriority
        ?? params.data?.entity_priority
        ?? params.data?.priority,
      )
    ),
  },
  subscription: {
    headerName: 'Subscription',
    width: 78,
    valueGetter: (params) => params.data?.subscription ?? '',
  },
  irrigRec: {
    headerName: 'Irrig.\nRec.',
    wrapHeaderText: true,
    autoHeaderHeight: true,
    width: 70,
    valueGetter: (params) => getRatioSortValue(getRatioValues(
      params.data,
      RECOMMENDATIONS_HEALTHY_KEYS,
    )),
    cellRenderer: irrigRecCellRenderer,
    cellStyle: (params) => getGatewayStyleFromRatio(getRatioValues(params.data, [
      'recommendationsHealthyBlockCount',
      'recommendations_healthy_block_count',
      'recommendationsStatusHealthyBlockCount',
      'recHealthyBlockCount',
      'rec_healthy_block_count',
    ])),
    tooltipValueGetter: (params) => getRecommendationTooltip(
      params.data,
      RECOMMENDATIONS_HEALTHY_KEYS,
      RECOMMENDATIONS_ENABLED_KEYS,
      'Irrigation recommendations',
    ),
    cellClass: 'status-cell',
  },
  satelliteImg: {
    headerName: 'Satellite\nImg.',
    wrapHeaderText: true,
    autoHeaderHeight: true,
    width: 78,
    valueGetter: (params) => getRatioSortValue(getRatioValues(
      params.data,
      IMAGERY_HEALTHY_KEYS,
    )),
    cellRenderer: satelliteImgCellRenderer,
    cellStyle: (params) => getGatewayStyleFromRatio(getRatioValues(params.data, [
      'imageryHealthyBlockCount',
      'imagery_healthy_block_count',
      'imageryStatusHealthyBlockCount',
      'imagery_status_healthy_block_count',
    ])),
    tooltipValueGetter: (params) => getRecommendationTooltip(
      params.data,
      IMAGERY_HEALTHY_KEYS,
      IMAGERY_ENABLED_KEYS,
      'Satellite imagery',
    ),
    cellClass: 'status-cell',
  },
  location: {
    headerName: 'Location',
    width: 132,
    minWidth: 132,
    maxWidth: 180,
    valueGetter: (params) => getLocationValue(params.data),
    tooltipValueGetter: (params) => params.value || 'Location unavailable',
    cellRenderer: locationCellRenderer,
  },
  blockCount: {
    headerName: 'Block\nCount',
    wrapHeaderText: true,
    autoHeaderHeight: true,
    width: 50,
    valueGetter: (params) => getDisplayValue(params.data?.block_count ?? params.data?.blockCount),
  },
  gatewayConnected: {
    headerName: 'Gateway\nCount',
    wrapHeaderText: true,
    autoHeaderHeight: true,
    width: 66,
    valueGetter: (params) => getCountSortValue(getCountValues(
      params.data,
      ['gateway_online_count', 'gatewayOnlineCount'],
      ['gateway_total_count', 'gatewayTotalCount'],
    )),
    cellRenderer: (params) => connectedDeviceCellRenderer(getCountValues(
      params.data,
      ['gateway_online_count', 'gatewayOnlineCount'],
      ['gateway_total_count', 'gatewayTotalCount'],
    )),
    cellStyle: (params) => getCountStyle(getCountValues(
      params.data,
      ['gateway_online_count', 'gatewayOnlineCount'],
      ['gateway_total_count', 'gatewayTotalCount'],
    )),
    tooltipValueGetter: (params) => getConnectedDeviceTooltip('Gateways', getCountValues(
      params.data,
      ['gateway_online_count', 'gatewayOnlineCount'],
      ['gateway_total_count', 'gatewayTotalCount'],
    )),
    cellClass: 'status-cell',
  },
  nodeConnected: {
    headerName: 'Node\nCount',
    wrapHeaderText: true,
    autoHeaderHeight: true,
    width: 64,
    valueGetter: (params) => getCountSortValue(getCountValues(
      params.data,
      ['node_online_count', 'nodeOnlineCount'],
      ['node_total_count', 'nodeTotalCount'],
    )),
    cellRenderer: (params) => connectedDeviceCellRenderer(getCountValues(
      params.data,
      ['node_online_count', 'nodeOnlineCount'],
      ['node_total_count', 'nodeTotalCount'],
    )),
    cellStyle: (params) => getCountStyle(getCountValues(
      params.data,
      ['node_online_count', 'nodeOnlineCount'],
      ['node_total_count', 'nodeTotalCount'],
    )),
    tooltipValueGetter: (params) => getConnectedDeviceTooltip('Nodes', getCountValues(
      params.data,
      ['node_online_count', 'nodeOnlineCount'],
      ['node_total_count', 'nodeTotalCount'],
    )),
    cellClass: 'status-cell',
  },
  sensorConnected: {
    headerName: 'Sensor\nCount',
    wrapHeaderText: true,
    autoHeaderHeight: true,
    width: 68,
    valueGetter: (params) => getCountSortValue(getCountValues(
      params.data,
      ['sensor_online_count', 'sensorOnlineCount'],
      ['sensor_total_count', 'sensorTotalCount'],
    )),
    cellRenderer: (params) => connectedDeviceCellRenderer(getCountValues(
      params.data,
      ['sensor_online_count', 'sensorOnlineCount'],
      ['sensor_total_count', 'sensorTotalCount'],
    )),
    cellStyle: (params) => getCountStyle(getCountValues(
      params.data,
      ['sensor_online_count', 'sensorOnlineCount'],
      ['sensor_total_count', 'sensorTotalCount'],
    )),
    tooltipValueGetter: (params) => getConnectedDeviceTooltip('Sensors', getCountValues(
      params.data,
      ['sensor_online_count', 'sensorOnlineCount'],
      ['sensor_total_count', 'sensorTotalCount'],
    )),
    cellClass: 'status-cell',
  },
  anomalyScore: {
    headerName: 'Anomaly\nScore',
    wrapHeaderText: true,
    autoHeaderHeight: true,
    width: 58,
    valueGetter: (params) => getAnomalyScoreValue(params.data) ?? -1,
    cellRenderer: anomalyScoreCellRenderer,
    cellStyle: (params) => getAnomalyScoreStyle(getAnomalyScoreValue(params.data)),
    tooltipValueGetter: (params) => (
      getAnomalyScoreValue(params.data) === null
        ? 'Anomaly score unavailable'
        : `Anomaly score: ${getAnomalyScoreValue(params.data)}`
    ),
    cellClass: 'status-cell',
  },
  dataIssues: {
    headerName: 'Data Issues',
    valueGetter: (params) => getDisplayValue(
      params.data?.data_issues ?? params.data?.dataIssues,
    ),
  },
  pendingAction: {
    headerName: 'Pending Action',
    valueGetter: (params) => getDisplayValue(
      params.data?.pending_action ?? params.data?.pendingAction,
    ),
  },
  regionCountry: {
    headerName: 'Region\n(country)',
    wrapHeaderText: true,
    autoHeaderHeight: true,
    width: 66,
    valueGetter: (params) => (
      params.data?.region_country ?? params.data?.regionCountry ?? ''
    ),
  },
  actions: {
    headerName: 'Actions',
    pinned: 'right',
    width: 102,
    minWidth: 102,
    maxWidth: 102,
    suppressMenu: true,
    sortable: false,
    filter: false,
    cellRenderer: 'ranchActionsMenu',
  },
};

export const ranchMobileColumnDefs = {
  ...ranchColumnDefs,
  index: {
    ...ranchColumnDefs.index,
    pinned: 'left',
  },
  ranchId: {
    ...ranchColumnDefs.ranchId,
    pinned: 'left',
  },
  name: {
    ...ranchColumnDefs.name,
    pinned: null,
  },
  entityName: {
    ...ranchColumnDefs.entityName,
    pinned: null,
  },
  actions: {
    ...ranchColumnDefs.actions,
    pinned: null,
  },
};
